import { config as defaults } from '@/option/config'
import { createBlankPage } from '@/schema/page-project'
import { defaultCanvasFilter } from '@/schema/page-config'
import { normalizeTemplate } from '@/utils/templates'
import { instantiateTemplate } from '@/editor/core/component-template'
import { pageDeletionState } from '@/editor/core/page-deletion'
import { createPageCopy } from '@/editor/core/page-copy'
import { uuid } from '@/utils/utils'
import store from '@/store'

export default {
  methods: {
    syncActivePage () {
      const page = this.pageProject.pages.find(page => page.id === this.activePageId)
      if (page) { page.config = this.config; page.nodes = this.nav }
    },
    switchPage (id) {
      if (!this.projectRecord || id === this.activePageId) return
      this.rememberStructure()
      this.bindPage(id)
      // 切换视图不产生撤销步骤，但先收录上一页尚未进入历史的编辑。
      this.historyBaseline = this.editorSnapshot()
    },
    bindPage (id) {
      const page = this.pageProject.pages.find(page => page.id === id)
      if (!page) return
      this._pageTemplateContext = (this._pageTemplateContext || 0) + 1
      this.handleInitActive()
      this.overactive = ''
      if (this.$refs.contentmenu) this.$refs.contentmenu.hide()
      if (this.$refs.container) this.$refs.container.stopPan()
      this.code.box = false
      this.dialogVisible = false
      this.deviceVisible = false
      this.device = ''
      this.deviceId = ''
      this.deviceName = ''
      this.propertyList = []
      this.functionList = []
      this.otherPropertyList = []
      this.pageDimensionError = ''
      this.backgroundUploadError = ''
      if (!this.backgroundUploading) this.backgroundUploadFile = ''
      this.activePageId = id
      this.config = page.config
      this.nav = page.nodes
      this.$nextTick(() => {
        if (this.$refs.container) this.$refs.container.setScale()
      })
    },
    addPage () {
      if (!this.projectRecord) return
      this.rememberStructure()
      const page = createBlankPage(this.pageProject.pages, defaults)
      this.pageProject.pages.push(page)
      this.bindPage(page.id)
      this.rememberStructure()
      return page.id
    },
    copyPage (id) {
      if (!this.projectRecord || this.editorSaveReady === false || this.editorVersionSwitching) return false
      const index = this.pageProject.pages.findIndex(page => page.id === id)
      if (index < 0) return false
      const project = { ...this.pageProject, pages: this.pageProject.pages.map(page => page.id === this.activePageId ? { ...page, config: this.config, nodes: this.nav } : page) }
      const limits = this.isTemplateEditing ? { maxPages: 50, maxNodes: 1000 } : {}
      const page = createPageCopy(project, project.pages[index], uuid, undefined, limits)
      this.rememberStructure()
      this.pageProject.pages.splice(index + 1, 0, page)
      this.bindPage(page.id)
      this.rememberStructure()
      return page.id
    },
    movePage (id, direction) {
      if (!this.projectRecord || this.editorSaveReady === false || this.editorVersionSwitching) return false
      if (!['up', 'down'].includes(direction)) return false
      const pages = this.pageProject.pages, index = pages.findIndex(page => page.id === id)
      const target = index + (direction === 'up' ? -1 : 1)
      if (index < 0 || target < 0 || target >= pages.length) return false
      this.rememberStructure()
      const page = pages.splice(index, 1)[0]
      pages.splice(target, 0, page)
      this.rememberStructure()
      return true
    },
    async applyPageTemplate (item) {
      const allowed = () => store.getters['auth/hasResourcePermission']('visualizationTemplate:query')
      if (!this.projectRecord || !allowed() || this._applyingPageTemplate) return false
      this._applyingPageTemplate = true
      try {
        const template = normalizeTemplate(item)
        if (template.kind !== 'page') throw new Error('请选择页面模板')
        const project = this.pageProject, record = this.projectRecord, projectId = this.id, pageId = this.activePageId
        const page = project.pages.find(page => page.id === pageId)
        if (!page) throw new Error('当前页面不存在')
        const context = this._pageTemplateContext, config = this.config, nav = this.nav
        await this.$confirm('应用“' + template.name + '”将替换当前页面“' + page.name + '”的尺寸、背景、水印、滤镜、说明和全部组件。页面名称及其他页面保留，应用后可撤销。是否继续？', '应用页面模板', { confirmButtonText: '替换当前页', cancelButtonText: '取消', type: 'warning', closeOnClickModal: false, customClass: 'editor-page-template-dialog' })
        // 确认期间切页、切项目、撤销或权限撤回，丢弃此次应用。
        if (this._isDestroyed || !allowed() || this.id !== projectId || this.projectRecord !== record || this.pageProject !== project || this.activePageId !== pageId || this._pageTemplateContext !== context || this.config !== config || this.nav !== nav || !project.pages.includes(page)) return false
        const nodes = instantiateTemplate(template.snapshot, uuid)
        const nextConfig = { ...config, backgroundColor: defaults.backgroundColor, backgroundImage: '', fitType: '等比例', canvasSize: 'custom', info: '', mark: { ...defaults.mark }, filter: { ...defaultCanvasFilter }, ...template.snapshot.config }
        this.rememberStructure()
        page.config = nextConfig; page.nodes = nodes
        this.bindPage(pageId)
        this.rememberStructure()
        this.$message.success('页面模板已应用并恢复数据源配置，可撤销；请检查设备绑定与页面跳转')
        return true
      } catch (error) {
        if (error !== 'cancel' && error !== 'close' && !this._isDestroyed) this.$message.error(error.message || '页面模板应用失败')
        return false
      } finally { this._applyingPageTemplate = false }
    },
    renamePage (id, name) {
      const page = this.pageProject.pages.find(page => page.id === id)
      name = String(name || '').trim()
      if (!page || !name || name.length > 60 || page.name === name) return false
      this.rememberStructure()
      page.name = name
      this.$set(page.config, 'name', name)
      this.rememberStructure()
      return true
    },
    setHomePage (id) {
      if (!this.pageProject.pages.some(page => page.id === id) || this.pageProject.homePageId === id) return
      this.rememberStructure()
      this.pageProject.homePageId = id
      this.rememberStructure()
    },
    deletePage (id, replacementHomeId) {
      if (!this.projectRecord || this.editorSaveReady === false || this.editorVersionSwitching) return false
      // 提交时重新检查，不能只依赖弹框打开时的结果或按钮禁用状态。
      const state = pageDeletionState(this.pageProject, id, { activePageId: this.activePageId, nodes: this.nav, replacementHomeId })
      if (state.error) return false
      const pages = this.pageProject.pages
      const index = pages.findIndex(page => page.id === id)
      this.rememberStructure()
      pages.splice(index, 1)
      if (state.isHome) this.pageProject.homePageId = replacementHomeId
      if (this.activePageId === id) this.bindPage(state.isHome ? replacementHomeId : pages[Math.min(index, pages.length - 1)].id)
      this.rememberStructure()
      return true
    }
  }
}
